import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Fab,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { FileUpload } from "@mui/icons-material";

import StickySearchBar from "@/components/mobile/StickySearchBar";
import { V2_COLORS } from "../constants/colors";
import MobileFilterDrawer from "@/components/mobile/MobileFilterDrawer";
import MobilePagination from "@/components/mobile/MobilePagination";
import FilterChip from "@/components/mobile/FilterChip";
import {
  INVERTED_RANGE_MESSAGE,
  invertedRangeToKeys,
  rangeInputProps,
} from "@/lib/dateRange";

import MobileInvoiceCard from "./MobileInvoiceCard";
import type { V2InvoiceListItem } from "../types/invoice";
import type { SidebarFilterItem } from "../constants/sidebarFilters";

export interface ExtraFilterDef {
  title: string;
  filter_key?: string;
  type: string;
  placeholder?: string;
  filters?: Array<{ label: string; value?: string; filterKey?: string }>;
}

interface MobileInvoiceListProps {
  invoices: V2InvoiceListItem[];
  totalCount: number;
  pageSize: number;
  page: number;
  onPageChange: (page: number) => void;
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  onSearch: () => void;
  onSearchClear: () => void;
  isInternal: boolean;
  canUpload: boolean;
  statusFilters: SidebarFilterItem[];
  selectedStatus: string | null;
  statusCounts: Record<string, number>;
  summaryTotalCount: number;
  onStatusChange: (status: string | null) => void;
  onRowClick: (invoice: V2InvoiceListItem) => void;
  onReview: (invoice: V2InvoiceListItem) => void;
  onEdit: (invoice: V2InvoiceListItem) => void;
  onCreditNote: (invoice: V2InvoiceListItem) => void;
  activeBatchCount: number;
  onUploadOpen: () => void;
  isFetching: boolean;
  rejectedCount: number;
  extraFilterData: ExtraFilterDef[];
  extraFilters: Record<string, unknown>;
  onExtraFilterChange: (filters: Record<string, unknown>) => void;
  cities: Array<{ id: string | number; display_name: string }>;
  citiesLoading: boolean;
}

export default function MobileInvoiceList({
  invoices,
  totalCount,
  pageSize,
  page,
  onPageChange,
  searchInput,
  onSearchInputChange,
  onSearch,
  onSearchClear,
  isInternal,
  canUpload,
  statusFilters,
  selectedStatus,
  statusCounts,
  summaryTotalCount,
  onStatusChange,
  onRowClick,
  onReview,
  onEdit,
  onCreditNote,
  activeBatchCount,
  onUploadOpen,
  isFetching,
  rejectedCount,
  extraFilterData,
  extraFilters,
  onExtraFilterChange,
  cities,
  citiesLoading,
}: MobileInvoiceListProps) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(selectedStatus);
  const [pendingExtraFilters, setPendingExtraFilters] = useState<Record<string, unknown>>({});
  const listRef = useRef<HTMLDivElement>(null);

  const totalPages = Math.ceil(totalCount / pageSize);
  // A To before its From matches nothing; Apply waits until it is fixed (ENG-28341).
  const invertedToKeys = invertedRangeToKeys(extraFilterData, pendingExtraFilters);

  // Scroll card list to top when page changes
  useEffect(() => {
    listRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
  }, [page]);

  const selectedFilterLabel = statusFilters.find((f) => f.status === selectedStatus)?.label;

  /** Build descriptors for active extra filter chips shown above the list. */
  const activeExtraChips = useMemo(() => {
    const chips: Array<{ key: string; label: string; value: string; filterKeys: string[] }> = [];
    for (const fd of extraFilterData) {
      if (fd.type === "rangeDate") {
        const dateKeys = fd.filters?.map((f) => f.filterKey!) ?? [];
        const dateValues = dateKeys.map((k) => extraFilters[k] as string).filter(Boolean);
        if (dateValues.length > 0) {
          chips.push({ key: "date_range", label: fd.title, value: dateValues.join(" \u2013 "), filterKeys: dateKeys });
        }
      } else if (fd.type === "checkbox" && fd.filter_key) {
        const val = extraFilters[fd.filter_key];
        if (Array.isArray(val) && val.length > 0) {
          const labels = fd.filters?.filter((f) => val.includes(f.value!)).map((f) => f.label) ?? val;
          chips.push({ key: fd.filter_key, label: fd.title, value: labels.join(", "), filterKeys: [fd.filter_key] });
        }
      } else if ((fd.type === "text" || fd.type === "select") && fd.filter_key) {
        const val = extraFilters[fd.filter_key];
        if (typeof val === "string" && val) {
          chips.push({ key: fd.filter_key, label: fd.title, value: val, filterKeys: [fd.filter_key] });
        }
      }
    }
    return chips;
  }, [extraFilterData, extraFilters]);

  const activeFilterCount = (selectedStatus ? 1 : 0) + activeExtraChips.length;

  // --- Drawer handlers ---

  const handleFilterOpen = useCallback(() => {
    setPendingStatus(selectedStatus);
    setPendingExtraFilters({ ...extraFilters });
    setFilterOpen(true);
  }, [selectedStatus, extraFilters]);

  const handleFilterApply = useCallback(() => {
    onStatusChange(pendingStatus);
    onExtraFilterChange(pendingExtraFilters);
  }, [onStatusChange, pendingStatus, onExtraFilterChange, pendingExtraFilters]);

  const handleFilterReset = useCallback(() => {
    setPendingStatus(null);
    setPendingExtraFilters({});
  }, []);

  const handleSearchClear = useCallback(() => {
    onSearchInputChange("");
    onSearchClear();
  }, [onSearchInputChange, onSearchClear]);

  const handleRemoveExtraChip = useCallback(
    (filterKeys: string[]) => {
      const next = { ...extraFilters };
      for (const k of filterKeys) delete next[k];
      onExtraFilterChange(next);
    },
    [extraFilters, onExtraFilterChange],
  );

  // --- Pending extra filter mutators ---

  const handlePendingCheckboxToggle = useCallback((filterKey: string, value: string) => {
    setPendingExtraFilters((prev) => {
      const current = Array.isArray(prev[filterKey]) ? [...(prev[filterKey] as string[])] : [];
      const idx = current.indexOf(value);
      if (idx > -1) current.splice(idx, 1);
      else current.push(value);
      return { ...prev, [filterKey]: current };
    });
  }, []);

  const handlePendingValueChange = useCallback((filterKey: string, value: string) => {
    setPendingExtraFilters((prev) => ({ ...prev, [filterKey]: value }));
  }, []);

  /** Count for a status filter item: sum of its lifecycle statuses, or summaryTotalCount for "All". */
  const getFilterCount = (filter: SidebarFilterItem): number => {
    if (filter.status === null) return summaryTotalCount;
    return filter.lifecycleStatuses.reduce((sum, ls) => sum + (statusCounts[ls] ?? 0), 0);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Search + filter */}
      <StickySearchBar
        value={searchInput}
        onChange={onSearchInputChange}
        onSubmit={searchInput ? onSearch : handleSearchClear}
        onClear={handleSearchClear}
        onFilterClick={handleFilterOpen}
        filterBadgeCount={activeFilterCount}
        placeholder="Search by Vendor, Order or Invoice ID"
      />

      {/* Heading — mirrors the desktop "Invoice List (count)" */}
      <Box display="flex" alignItems="baseline" gap={1} sx={{ px: 2, pt: 1.5 }}>
        <Typography fontWeight={700} fontSize="16px" lineHeight="24px">
          Invoice List
        </Typography>
        <Typography fontSize="12px" lineHeight="16px" color={V2_COLORS.textSecondary}>
          ({totalCount})
        </Typography>
      </Box>

      {/* Rejected alert (vendor only) */}
      {rejectedCount > 0 && (
        <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
          {rejectedCount} invoice(s) rejected — review remarks and resubmit.
        </Alert>
      )}

      {/* Active filter chips */}
      {((selectedStatus && selectedFilterLabel) || activeExtraChips.length > 0) && (
        <Box sx={{ px: 2, pt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {selectedStatus && selectedFilterLabel && (
            <FilterChip label="Status" value={selectedFilterLabel} onRemove={() => onStatusChange(null)} />
          )}
          {activeExtraChips.map((chip) => (
            <FilterChip
              key={chip.key}
              label={chip.label}
              value={chip.value}
              onRemove={() => handleRemoveExtraChip(chip.filterKeys)}
            />
          ))}
        </Box>
      )}

      {/* Refetch indicator */}
      {isFetching && <LinearProgress sx={{ mx: 2, mt: 1, borderRadius: "4px" }} />}

      {/* Card list */}
      <Box
        ref={listRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          px: 2,
          pt: 1,
          pb: canUpload ? "80px" : 1,
        }}
      >
        {invoices.length === 0 ? (
          <Typography color="textSecondary" sx={{ textAlign: "center", py: 6 }}>
            No invoices found
          </Typography>
        ) : (
          invoices.map((invoice) => (
            <MobileInvoiceCard
              key={invoice.id}
              invoice={invoice}
              isInternal={isInternal}
              onClick={onRowClick}
              onReview={onReview}
              onEdit={onEdit}
              onCreditNote={onCreditNote}
            />
          ))
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <MobilePagination
          page={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
          totalItems={totalCount}
          pageSize={pageSize}
        />
      )}

      {/* Upload FAB */}
      {canUpload && (
        <Fab
          color="primary"
          size="medium"
          onClick={onUploadOpen}
          aria-label="Upload invoices"
          sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 20 }}
        >
          <Badge
            badgeContent={activeBatchCount}
            color="error"
            invisible={activeBatchCount === 0}
            sx={{ "& .MuiBadge-badge": { fontSize: "10px", height: 16, minWidth: 16 } }}
          >
            <FileUpload />
          </Badge>
        </Fab>
      )}

      {/* Filter drawer */}
      <MobileFilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={handleFilterApply}
        applyDisabled={invertedToKeys.length > 0}
        onReset={handleFilterReset}
        title="Filters"
      >
        {/* Status section */}
        <Typography sx={{ fontSize: "14px", fontWeight: 600, mb: 1 }}>Invoice Status</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {statusFilters.map((filter) => {
            const count = getFilterCount(filter);
            const isActive = filter.status === pendingStatus;
            return (
              <Chip
                key={filter.status ?? "all"}
                label={`${filter.label} (${count})`}
                variant={isActive ? "filled" : "outlined"}
                color={isActive ? "primary" : "default"}
                onClick={() => setPendingStatus(filter.status)}
                sx={{ fontSize: "13px", height: 32 }}
              />
            );
          })}
        </Box>

        {/* Extra filters */}
        {extraFilterData.length > 0 && <Divider sx={{ my: 2 }} />}

        {extraFilterData.map((filterItem) => (
          <Box key={filterItem.filter_key ?? filterItem.title} sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, mb: 1 }}>{filterItem.title}</Typography>

            {filterItem.type === "checkbox" && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {filterItem.filters?.map((opt) => {
                  const selected =
                    Array.isArray(pendingExtraFilters[filterItem.filter_key!]) &&
                    (pendingExtraFilters[filterItem.filter_key!] as string[]).includes(opt.value!);
                  return (
                    <Chip
                      key={opt.value}
                      label={opt.label}
                      variant={selected ? "filled" : "outlined"}
                      color={selected ? "primary" : "default"}
                      onClick={() => handlePendingCheckboxToggle(filterItem.filter_key!, opt.value!)}
                      sx={{ fontSize: "13px", height: 32 }}
                    />
                  );
                })}
              </Box>
            )}

            {filterItem.type === "text" && (
              <TextField
                value={(pendingExtraFilters[filterItem.filter_key!] as string) ?? ""}
                onChange={(e) => handlePendingValueChange(filterItem.filter_key!, e.target.value)}
                placeholder={filterItem.placeholder}
                size="small"
                fullWidth
              />
            )}

            {filterItem.type === "select" &&
              (citiesLoading ? (
                <CircularProgress size={20} data-testid="cities-loading" />
              ) : (
                <Select
                  value={(pendingExtraFilters[filterItem.filter_key!] as string) ?? ""}
                  onChange={(e) => handlePendingValueChange(filterItem.filter_key!, e.target.value as string)}
                  displayEmpty
                  size="small"
                  fullWidth
                  renderValue={(selected) => (selected as string) || filterItem.placeholder}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {cities.map((city) => (
                    <MenuItem key={city.id} value={city.display_name}>
                      {city.display_name}
                    </MenuItem>
                  ))}
                </Select>
              ))}

            {filterItem.type === "rangeDate" && (
              <Box sx={{ display: "flex", gap: 1 }}>
                {filterItem.filters?.map((df, index) => (
                  <TextField
                    key={df.filterKey}
                    inputProps={rangeInputProps(filterItem, index, pendingExtraFilters)}
                    error={invertedToKeys.includes(df.filterKey!)}
                    helperText={invertedToKeys.includes(df.filterKey!) ? INVERTED_RANGE_MESSAGE : undefined}
                    value={(pendingExtraFilters[df.filterKey!] as string) ?? ""}
                    onChange={(e) => handlePendingValueChange(df.filterKey!, e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    label={df.label}
                    type="date"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                ))}
              </Box>
            )}
          </Box>
        ))}
      </MobileFilterDrawer>
    </Box>
  );
}
